import type { Variants } from 'motion/react';
import type { HTMLAttributeAnchorTarget } from 'react';
import type { To } from 'react-router';
import { ContextMenu } from '@base-ui/react/context-menu';
import { Menu } from '@base-ui/react/menu';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useId } from 'react';
import { useNavigate } from 'react-router';

import type { IconName } from '../../assets/Icon/Icon';
import type { BadgeProps, BadgeSize } from '../../components/Badge/Badge';
import type {
  PolymorphicComponentProps,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { ControlSize } from '../shared/types';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import { containers } from '../../common/containers';
import { devices } from '../../common/devices';
import { Badge } from '../../components/Badge/Badge';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
} from '../../components/DropdownMenu/DropdownMenu';
import { IconTooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, shadows, spacings, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { isEmptyNode } from '../../utilities/isEmptyNode';
import { HStack } from '../../utilities/Stack/HStack';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { selectors, transitions } from '../shared/styles';
import {
  controlBorderRadius,
  controlGap,
  controlHeight,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlPreset,
} from '../shared/vars';
import { UtilityControl } from '../UtilityControl/UtilityControl';

const TabIcon = styled(Icon, {
  size: controlIconSize,
  color: '$$tabIconColor',
});
TabIcon.displayName = 'TabIcon';

const TabLabel = styled(Text, {
  color: '$$tabLabelColor',
  fontWeight: '$bold',
});
TabLabel.displayName = 'TabLabel';

const TabContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: controlGap,
  heightAll: controlHeight,
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  whiteSpace: 'nowrap',
  transition: transitions.control,

  [selectors.hover]: {
    backgroundColor: colors.bgNeutralLight,
    $$tabLabelColor: colors.headingNeutralLight,
    $$tabIconColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      $$tabLabelColor: colors.headingNeutralDark,
      $$tabIconColor: colors.bodyNeutralDark,
    },
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    hasAnnotation: {
      true: {},
      false: {},
    },
    hasSubtabs: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    isSelected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$tabLabelColor: colors.headingBrandLight,
          $$tabIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$tabLabelColor: colors.headingBrandDark,
            $$tabIconColor: colors.bodyBrandDark,
          },
        },
      },
      false: {
        $$tabLabelColor: colors.headingNeutralLight,
        $$tabIconColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$tabLabelColor: colors.headingNeutralDark,
          $$tabIconColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          $$tabLabelColor: colors.headingNeutralLight,
          $$tabIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$tabLabelColor: colors.headingNeutralDark,
            $$tabIconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
    ...controlVars,
  },
  compoundVariants: [
    {
      size: 'x-small',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'small',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'medium',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'large',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$8',
        },

        [devices.mouse]: {
          paddingRight: '$6',
        },
      },
    },
    {
      size: 'x-large',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$12',
        },

        [devices.mouse]: {
          paddingRight: '$10',
        },
      },
    },
    {
      hasSubtabs: false,
      isSelected: true,
      css: {},
    },
    {
      internal: true,
      css: {
        $$tabLabelColor: colors.internalBodyLight,
        $$tabIconColor: colors.internalIconLight,

        [selectors.hover]: {
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,
        },

        [darkThemeSelector]: {
          $$tabLabelColor: colors.internalBodyDark,
          $$tabIconColor: colors.internalIconDark,

          [selectors.hover]: {
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
    {
      internal: true,
      isSelected: true,
      css: {
        [selectors.all]: {
          backgroundColor: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
  ],
});
TabContainer.displayName = 'TabContainer';

type TabPropSize = ControlSize;
export interface TabProps {
  /**
   * The level required to access the tab.
   */
  accessLevel?: AccessLevel | null;
  /**
   * Additional annotation such a result count within the tab.
   * */
  annotation?: React.ReactNode | BadgeProps;
  /**
   * Written label for the tab.
   * */
  children?: React.ReactNode;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Key for the tab.
   */
  key?: string;
  /**
   * Written label for the tab.
   */
  label?: React.ReactNode;
  /**
   * Handles the onClick event.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Marks the currently selected tab.
   */
  selected?: boolean;
  /**
   * Whether the tab is disabled.
   */
  disabled?: boolean;
  /**
   * Size of the tab.
   */
  size?: TabPropSize;
  /**
   * Whether the tab has subtabs.
   */
  subtabs?: TabProps[];
  /**
   * A tooltip to be displayed on hover.
   */
  tooltip?: React.ReactNode;
  /**
   * Routing
   */
  as?: React.ElementType;
  to?: To;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
}

function filterOutNullAccessLevels(tab: TabProps): tab is typeof tab & {
  accessLevel: NonNullable<(typeof tab)['accessLevel']> | undefined;
} {
  return tab.accessLevel !== null;
}

function hasAnnotationContent(annotation: TabProps['annotation']): boolean {
  if (!annotation) return false;
  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return true;
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    return true;
  }
  return false;
}

function tabsSizeToBadgeSize(size: TabPropSize): BadgeSize {
  if (size === 'x-small') return 'x-small';
  if (size === 'small') return 'small';
  return 'medium';
}

function TabAnnotation({
  accessLevel: accessLevelProp,
  annotation,
  internal,
  size,
  selected,
}: {
  accessLevel?: AccessLevelProp | null;
  annotation: TabProps['annotation'];
  internal: boolean;
  size: TabPropSize;
  selected: boolean;
}) {
  const badgeSize = tabsSizeToBadgeSize(size);
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;

  if (!annotation) return null;

  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return (
      <Badge internal={isInternal} size={badgeSize} variant={selected ? 'brand' : 'neutral'}>
        {annotation}
      </Badge>
    );
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    const annotationVariant = annotation.variant || 'neutral';
    return (
      <Badge
        {...annotation}
        arrangement={annotation.arrangement ?? 'leading-icon'}
        internal={isInternal}
        size={badgeSize}
        variant={!annotation.variant && selected ? 'brand' : annotationVariant}
      >
        {annotation.label}
      </Badge>
    );
  }

  return null;
}

function TabInner<Tag extends React.ElementType>(
  {
    accessLevel: accessLevelProp,
    annotation,
    children,
    icon,
    internal,
    label,
    selected = false,
    disabled = false,
    size = 'medium',
    subtabs,
    tooltip,
    onClick,
    as,
    to,
    href,
    target,
    ...remaining
  }: PolymorphicComponentProps<Tag, TabProps>,
  forwardedRef: PolymorphicRef<Tag>,
) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;
  if (accessLevel === null) return null;

  const hasEnd =
    hasAnnotationContent(annotation) ||
    (accessLevel && isAccessLevelVisible(accessLevel)) ||
    isDefined(tooltip);

  return (
    <TabContainer
      type="button"
      role="tab"
      aria-selected={selected ? 'true' : 'false'}
      ref={forwardedRef}
      isSelected={selected}
      hasAnnotation={hasAnnotationContent(annotation)}
      hasSubtabs={isDefined(subtabs)}
      internal={isInternal}
      size={size}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      // TODO: react-three-fiber's global JSX augmentation overflows
      // TabContainer's Stitches polymorphic `as` union (TS2590) in any consumer
      // that also pulls in r3f (e.g. @meterup/spatial). atto's own type-check
      // has no r3f, so there's no error here — an expect-error directive would
      // be reported unused when atto builds standalone, hence ts-ignore. Remove
      // once atto ships built declarations.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- TS2590, see TODO above
      as={disabled ? undefined : as}
      to={disabled ? undefined : to}
      href={disabled ? undefined : href}
      target={disabled ? undefined : target}
      {...remaining}
    >
      <AlignStack
        direction="row"
        gap={size === 'x-small' ? 4 : 6}
        preset={getControlPreset(size)}
        start={isDefined(icon) && <TabIcon icon={icon} />}
        end={
          hasEnd && (
            <HStack align="center" spacing={4}>
              {tooltip && (
                <IconTooltip
                  icon="question"
                  size={14}
                  contents={tooltip}
                  variant={selected ? 'brand' : 'neutral'}
                />
              )}
              {isDefined(annotation) && (
                <TabAnnotation
                  accessLevel={accessLevel}
                  annotation={annotation}
                  internal={isInternal}
                  size={size}
                  selected={selected}
                />
              )}
              {accessLevel && isAccessLevelVisible(accessLevel) && (
                <FeatureBadge type={accessLevel} size="x-small" />
              )}
            </HStack>
          )
        }
      >
        <TabLabel>{label || children}</TabLabel>
      </AlignStack>
    </TabContainer>
  );
}

export const Tab = React.forwardRef(TabInner) as <D extends React.ElementType = 'button'>(
  props: PolymorphicComponentProps<D, TabProps> & { ref?: PolymorphicRef<D> },
) => ReturnType<typeof TabInner>;

const TabDivider = styled(Divider, {
  padding: '$4',
});
TabDivider.displayName = 'TabDivider';

const TabsViewWide = styled('div', {
  display: 'contents',

  [containers.application.notWide]: {
    display: 'none',
  },
});
TabsViewWide.displayName = 'TabsViewWide';

const TabsViewNarrow = styled('div', {
  display: 'none',

  [containers.application.notWide]: {
    display: 'block',
    width: '100%',
  },
});
TabsViewNarrow.displayName = 'TabsViewNarrow';

const TabsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
  alignItems: 'center',
  height: '100%',

  '@notDesktop': {
    width: '100%',
  },
});
TabsContainer.displayName = 'TabsContainer';
export type TabsProps = {
  /**
   * Pass `Tab` components into this for automatically laid out tabs.
   * */
  children?: React.ReactNode;
  /**
   * Size of the tabs.
   */
  size?: TabPropSize;
  /**
   * Tabs passed in as an array.
   */
  tabs?: TabProps[];
  /**
   * Secondary tabs passed in as an array.
   */
  secondaryTabs?: TabProps[];
  /**
   * If only one tab is visible, don't display it.
   */
  hideSingleTab?: boolean;
  /**
   * Menu items to show when a tab is right-clicked or long-pressed, or from its quick actions in
   * the narrow tabs dropdown. Return nothing to keep the browser's own context menu. `parent` is
   * set for subtabs.
   */
  renderTabMenu?: (tab: TabProps, parent?: TabProps) => React.ReactNode;
};

function TabContextMenu({
  menu,
  children,
}: {
  menu: React.ReactNode;
  children: React.ReactElement;
}) {
  if (isEmptyNode(menu)) return children;

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger render={children} />
      <DropdownMenuPopover align="start" side="bottom" sideOffset={0}>
        {menu}
      </DropdownMenuPopover>
    </ContextMenu.Root>
  );
}

const TabDropdownRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$4',

  '& > :first-child': {
    flex: 1,
    minWidth: 0,
  },
});
TabDropdownRow.displayName = 'TabDropdownRow';

function TabDropdownActions({
  menu,
  internal,
  children,
}: {
  menu: React.ReactNode;
  internal?: boolean;
  children: React.ReactElement;
}) {
  if (isEmptyNode(menu)) return children;

  return (
    <TabDropdownRow>
      {children}
      <Menu.SubmenuRoot>
        <Menu.SubmenuTrigger
          nativeButton
          openOnHover={false}
          render={
            <UtilityControl icon="overflow-horizontal" internal={internal} label="Quick actions" />
          }
        />
        <DropdownMenuPopover align="end" side="bottom">
          {menu}
        </DropdownMenuPopover>
      </Menu.SubmenuRoot>
    </TabDropdownRow>
  );
}

function tabAnnotationToBadge(
  annotation: TabProps['annotation'],
): { label: React.ReactNode } | undefined {
  if (!annotation) return undefined;
  if (typeof annotation !== 'object') return { label: annotation };
  if ('label' in annotation && annotation.label != null) return { label: annotation.label };
  return undefined;
}

const TAB_GROUP_INSET = `-${spacings[2]}`;
// control border radius + inset
const TAB_GROUP_BORDER_RADIUS = `calc(${controlBorderRadius} + ${spacings[2]})`;

const RealTabWrapper = styled('div', {
  variants: {
    hasSubtabs: {
      true: {
        position: 'relative',
        isolation: 'isolate',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',

        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          inset: TAB_GROUP_INSET,
          borderRadius: TAB_GROUP_BORDER_RADIUS,
          backgroundColor: 'transparent',
          strokeAll: 'transparent',
          transition: transitions.control,
          pointerEvents: 'none',
        },
      },
      false: {
        display: 'contents',
      },
    },
    isGrouped: {
      true: {
        '&::before': {
          backgroundColor: colors.bgNeutralLight,
          strokeAll: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
            strokeAll: colors.strokeNeutralDark,
          },
        },
      },
      false: {},
    },
    ...controlVars,
  },
  defaultVariants: {
    size: 'medium',
  },
});
RealTabWrapper.displayName = 'RealTabWrapper';

const TabSubtabs = styled(motion.div, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  height: '100%',
  overflowX: 'clip',
  overflowY: 'visible',
  willChange: 'auto',

  '& > *': {
    marginLeft: '$4',
  },
});
TabSubtabs.displayName = 'TabSubtabs';

const SUBTAB_ANIMATION = {
  initial: {
    width: 0,
    opacity: 0,
    x: -4,
  },
  animate: {
    width: 'auto',
    opacity: 1,
    x: 0,
  },
} as const satisfies Variants;

function TabsArray({ size, tabs, secondaryTabs, hideSingleTab = true, renderTabMenu }: TabsProps) {
  const navigate = useNavigate();
  const key = useId();
  const selectedTab =
    tabs?.find((tab) => tab.selected) || secondaryTabs?.find((tab) => tab.selected);
  const selectedSubtab = selectedTab?.subtabs?.find((subtab) => subtab.selected);

  const onClickTab = useCallback(
    (tab: TabProps, e: React.MouseEvent<HTMLDivElement>) => {
      if (tab.to) {
        navigate(tab.to);
      } else if (tab.href) {
        window.open(tab.href, tab.target ?? '_self');
      }

      tab.onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [navigate],
  );

  const filteredTabs = tabs?.filter(filterOutNullAccessLevels);
  const filteredSecondaryTabs = secondaryTabs?.filter(filterOutNullAccessLevels);

  // Don't render needless tabs if there's only one option
  if (hideSingleTab && filteredTabs?.length === 1 && !filteredSecondaryTabs?.length) return null;

  return (
    <>
      <TabsViewWide>
        {filteredTabs?.map((tab) => {
          const hasSubtabs = !!tab.subtabs?.length;

          return (
            <RealTabWrapper
              key={`${key}-tab-${tab.label}`}
              hasSubtabs={hasSubtabs}
              isGrouped={hasSubtabs && !!tab.selected}
              size={size}
            >
              <TabContextMenu menu={renderTabMenu?.(tab)}>
                {/* TODO: react-three-fiber's global JSX augmentation overflows Tab's
                  polymorphic `as` union (TS2590) in any consumer that also pulls in
                  r3f (e.g. @meterup/spatial); atto's own type-check has no r3f so
                  there's no error here. Remove once atto ships built declarations. */}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore -- TS2590, see TODO above */}
                <Tab size={size} {...tab} />
              </TabContextMenu>
              <AnimatePresence initial={false}>
                {tab.selected && hasSubtabs ? (
                  <TabSubtabs
                    initial={SUBTAB_ANIMATION.initial}
                    exit={SUBTAB_ANIMATION.initial}
                    animate={SUBTAB_ANIMATION.animate}
                  >
                    <Icon icon="chevron-right" size={12} variant="brand" />
                    {tab.subtabs?.map((subtab) => (
                      <TabContextMenu
                        key={`${key}-subtab-${subtab.label}`}
                        menu={renderTabMenu?.(subtab, tab)}
                      >
                        <Tab size={size} {...subtab} />
                      </TabContextMenu>
                    ))}
                  </TabSubtabs>
                ) : null}
              </AnimatePresence>
            </RealTabWrapper>
          );
        })}
        {!!filteredTabs?.length && !!filteredSecondaryTabs?.length && (
          <TabDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {filteredSecondaryTabs?.map((secondaryTab) => (
          <React.Fragment key={`${key}-secondaryTab-${secondaryTab.label}`}>
            <TabContextMenu menu={renderTabMenu?.(secondaryTab)}>
              <Tab size={size} {...secondaryTab} />
            </TabContextMenu>
            {secondaryTab.selected && secondaryTab.subtabs?.length && (
              <>
                <Icon icon="chevron-right" size={12} variant="brand" />
                {secondaryTab.subtabs?.map((subtab) => (
                  <TabContextMenu
                    key={`${key}-subtab-${subtab.label}`}
                    menu={renderTabMenu?.(subtab, secondaryTab)}
                  >
                    <Tab size={size} {...subtab} />
                  </TabContextMenu>
                ))}
              </>
            )}
          </React.Fragment>
        ))}
      </TabsViewWide>
      <TabsViewNarrow>
        <DropdownMenu>
          <DropdownMenuButton
            appearance="muted"
            arrangement="leading-icon"
            icon={selectedTab?.icon}
            labelAlign="start"
            labelStretch
            size="medium"
            width="100%"
            annotation={
              typeof selectedTab?.annotation !== 'object' ? selectedTab?.annotation : undefined
            }
          >
            {selectedTab?.label} {selectedSubtab ? ` > ${selectedSubtab.label}` : null}
          </DropdownMenuButton>
          <DropdownMenuPopover align="start" matchTrigger>
            {!!filteredTabs?.length && (
              <DropdownMenuGroup>
                {filteredTabs.map((tab) => {
                  const hasSubtabs = tab.subtabs?.length;
                  if (hasSubtabs) {
                    return (
                      <DropdownMenuGroup key={`${key}-tab-${tab.label}`} label={tab.label} isNested>
                        {tab.subtabs?.map((subtab) => {
                          if (subtab.accessLevel === null) {
                            return null;
                          }
                          return (
                            <TabDropdownActions
                              key={`${key}-subtab-${subtab.label}`}
                              menu={renderTabMenu?.(subtab, tab)}
                              internal={tab.internal}
                            >
                              <DropdownMenuItem
                                onClick={(e) => onClickTab(subtab, e)}
                                accessLevel={subtab.accessLevel ?? undefined}
                                badge={tabAnnotationToBadge(subtab.annotation)}
                                icon={subtab.icon}
                                label={subtab.label || subtab.children}
                                internal={tab.internal}
                                tooltip={tab.tooltip}
                                selected={selectedSubtab?.label === subtab.label}
                                disabled={subtab.disabled}
                              />
                            </TabDropdownActions>
                          );
                        })}
                      </DropdownMenuGroup>
                    );
                  }

                  return (
                    <TabDropdownActions
                      key={`${key}-tab-${tab.label}`}
                      menu={renderTabMenu?.(tab)}
                      internal={tab.internal}
                    >
                      <DropdownMenuItem
                        onClick={(e) => onClickTab(tab, e)}
                        accessLevel={tab.accessLevel}
                        badge={tabAnnotationToBadge(tab.annotation)}
                        icon={tab.icon}
                        label={tab.label || tab.children}
                        internal={tab.internal}
                        tooltip={tab.tooltip}
                        selected={selectedTab?.label === tab.label}
                        disabled={tab.disabled}
                      />
                    </TabDropdownActions>
                  );
                })}
              </DropdownMenuGroup>
            )}
            {!!filteredSecondaryTabs?.length && (
              <DropdownMenuGroup>
                {filteredSecondaryTabs.map((secondaryTab) => (
                  <TabDropdownActions
                    key={`${key}-secondaryTab-${secondaryTab.label}`}
                    menu={renderTabMenu?.(secondaryTab)}
                    internal={secondaryTab.internal}
                  >
                    <DropdownMenuItem
                      onClick={(e) => onClickTab(secondaryTab, e)}
                      accessLevel={secondaryTab.accessLevel}
                      badge={tabAnnotationToBadge(secondaryTab.annotation)}
                      icon={secondaryTab.icon}
                      label={secondaryTab.label || secondaryTab.children}
                      internal={secondaryTab.internal}
                      tooltip={secondaryTab.tooltip}
                      selected={selectedTab?.label === secondaryTab.label}
                      disabled={secondaryTab.disabled}
                    />
                  </TabDropdownActions>
                ))}
              </DropdownMenuGroup>
            )}
          </DropdownMenuPopover>
        </DropdownMenu>
      </TabsViewNarrow>
    </>
  );
}

export function Tabs({
  children,
  size,
  tabs,
  secondaryTabs,
  hideSingleTab,
  renderTabMenu,
  ...remaining
}: TabsProps) {
  const isArray = Array.isArray(tabs) || Array.isArray(secondaryTabs);
  return (
    <TabsContainer role="tablist" {...remaining}>
      {isArray ? (
        <TabsArray
          size={size}
          tabs={tabs}
          secondaryTabs={secondaryTabs}
          hideSingleTab={hideSingleTab}
          renderTabMenu={renderTabMenu}
        />
      ) : (
        children || tabs || secondaryTabs
      )}
    </TabsContainer>
  );
}
